/**
 * MathCoachPage
 * Main page for the parent Math Coach chat interface at /math-coach.
 *
 * Conditional rendering:
 *   - Loading: while checking for active session or loading messages
 *   - No session (404): shows MathCoachGuidanceScreen
 *   - Error: shows ErrorState with retry
 *   - Active session: chat interface (image upload + message history + text input)
 *
 * Reuses:
 *   - useMathCoachSession (US-002): polls GET /sessions/math-coach/active
 *   - useSessionMessages: adaptive polling for message history (2s–10s backoff)
 *   - useCompleteSession: End Session button handler
 *   - useMathCoachMessages: sends text/image with optimistic UI
 *   - MessageBubble: displays tutor text messages
 *   - ImageMessageBubble: displays image messages
 *   - AnswerInput: text input with waiting/completed states
 *   - ImageUpload: image picker, validation, HEIC conversion
 *   - LoadingState, ErrorState, AppLogo: shared UI primitives
 *   - useViewportHeight: mobile keyboard height fix
 *
 * PRD Invariant 4: Zero impact on English session flows.
 * Parent role messages are mapped to 'student' for MessageBubble alignment
 * (Option A from the implementation guide) rather than modifying MessageBubble.
 *
 * AC coverage:
 *   AC-003.1 - File picker accepts JPEG, PNG, HEIC under 10MB (via ImageUpload)
 *   AC-003.2 - HEIC converted to JPEG client-side (via ImageUpload)
 *   AC-003.3 - Optimistic image + typing indicator (via useMathCoachMessages + typing logic)
 *   AC-003.4 - Text follow-up with optimistic UI (via AnswerInput + useMathCoachMessages)
 *   AC-003.5 - Files > 10MB rejected (via ImageUpload validation)
 *   AC-003.6 - Non-image files rejected (via ImageUpload validation)
 *   AC-003.7 - Rollback + error on network failure (via useMathCoachMessages onError)
 */

import { useRef, useEffect, useCallback } from 'react';
import { useScrollControl } from '@/hooks/useScrollControl';
import { NewMessagePill } from '@/components/NewMessagePill';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useMathCoachSession } from '@/hooks/useMathCoachSession';
import { useSessionMessages } from '@/hooks/useSessionMessages';
import { useCompleteSession } from '@/hooks/useCompleteSession';
import { useMathCoachMessages } from '@/hooks/useMathCoachMessages';
import { useViewportHeight } from '@/hooks/useViewportHeight';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { AppLogo } from '@/components/AppLogo';
import { Button } from '@/components/ui/button';
import { MessageBubble } from '@/components/MessageBubble';
import { ImageMessageBubble } from '@/components/ImageMessageBubble';
import { AnswerInput } from '@/components/AnswerInput';
import { ImageUpload } from '@/components/ImageUpload';
import { MathCoachGuidanceScreen } from '@/pages/MathCoachGuidanceScreen';
import { MathCoachTypingIndicator } from '@/components/MathCoachTypingIndicator';
import type { Message } from '@/types/session';

/**
 * MathCoachPage — parent-facing Math Coach chat interface.
 * Route: /math-coach (inside AuthGuard)
 */
export function MathCoachPage() {
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Monitor viewport height for mobile keyboard handling
  useViewportHeight();

  // ---- Step 1: Check for active math coach session ----
  // useMathCoachSession polls GET /sessions/math-coach/active every 5s when started.
  // For MathCoachPage we only need the one-shot check on mount (no explicit polling here).
  const {
    session: activeSession,
    isLoading: isCheckingSession,
    error: sessionCheckError,
    startPolling,
    stopPolling,
  } = useMathCoachSession();

  // Start polling on mount so we detect sessions created after landing on this page
  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const activeSessionId = activeSession?.sessionId ?? null;

  // ---- Step 2: Load session messages (only when we have a session ID) ----
  // Math Coach profile: 30s delay after image, 10s delay after text, fixed 5s interval,
  // no pause on tutor message.
  const {
    messages,
    status,
    isLoading: isLoadingMessages,
    error: messagesError,
    notifyPollDelay,
    pollingPhase,
  } = useSessionMessages(activeSessionId ?? 'placeholder', { sessionType: 'math_coach' });

  // ---- Step 3: Session completion ----
  const {
    complete: completeSession,
    isCompleting,
    isSuccess: isSessionCompleted_fromComplete,
    error: completeError,
  } = useCompleteSession(activeSessionId ?? '');

  // Navigate to dashboard when session completion succeeds (AC-006.1)
  useEffect(() => {
    if (isSessionCompleted_fromComplete) {
      navigate('/dashboard', { replace: true });
    }
  }, [isSessionCompleted_fromComplete, navigate]);

  // ---- Step 4: Message sending with optimistic UI ----
  // notifyPollDelay passed so the polling hook starts the correct delay after each send.
  const { sendTextMessage, sendImageMessage, isSending, sendError, clearError } =
    useMathCoachMessages(activeSessionId ?? '', notifyPollDelay);

  // ---- Scroll control: disable auto-scroll; show pill for new messages ----
  // AC-002.1: scroll position is never changed by new message arrival when parent
  // is reading above fold. AC-002.6: enabled=true is Math Coach-only; English
  // tutor (SessionChatPage) is completely unaffected by this hook.
  const { pillVisible, scrollToBottom } = useScrollControl(
    messageContainerRef,
    messages,
    true, // Math Coach always uses controlled scroll
  );

  // ---- Image ready handler: forward to mutation hook ----
  const handleImageReady = useCallback(
    (base64Data: string, mimeType: string, previewUrl: string) => {
      sendImageMessage(base64Data, mimeType, previewUrl);
    },
    [sendImageMessage]
  );

  // ---- Derived state ----
  // Implements TS-018 T5 / TS-018.h (CCM-01 reader, defensive regression-proofing).
  // MathCoach is out of commercial scope and the cancel path never writes 'cancelled'
  // to math_coach sessions. This guard treats any terminal status as done so a future
  // status='cancelled' value on a math_coach session does not leave the UI in an
  // interactive state (AC-6).
  const isSessionCompleted = status === 'completed' || status === 'cancelled';
  const lastMessage = messages && messages.length > 0 ? messages[messages.length - 1] : null;
  // Show typing indicator when last message is from parent (waiting for tutor)
  const isWaitingForTutor =
    !isSessionCompleted && lastMessage?.role === 'parent';

  // ---- State A: Session check in progress ----
  if (isCheckingSession) {
    return <LoadingState message="Loading Math Coach..." />;
  }

  // ---- State B: Non-404 error on session check ----
  if (sessionCheckError) {
    return (
      <ErrorState
        title="Failed to Load"
        message={sessionCheckError.message || 'Unable to check for active session.'}
        onRetry={() => startPolling()}
      />
    );
  }

  // ---- State C: No active session ----
  if (!activeSessionId) {
    return <MathCoachGuidanceScreen />;
  }

  // ---- State A2: Messages still loading ----
  if (isLoadingMessages && (!messages || messages.length === 0)) {
    return <LoadingState message="Loading Math Coach..." />;
  }

  // ---- State B2: Messages error ----
  if (messagesError && !messages) {
    return (
      <ErrorState
        title="Failed to Load Session"
        message={messagesError.message || 'Unable to load session messages.'}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // ---- State D: Active session — chat view ----
  return (
    <div
      className="min-h-screen landscape:h-screen lg:h-screen flex flex-col bg-otter-paper"
      style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}
    >
      {/* Header */}
      <header className="border-b border-[#E7D8C8] bg-otter-paper px-3 md:px-4 py-2 md:py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard"
            className="text-sm text-otter-earth-500 hover:text-otter-earth-700 flex items-center gap-0.5"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <AppLogo />
        </div>

        <div className="flex items-center gap-2">
          {/* End Session button */}
          {!isSessionCompleted && (
            <Button
              variant="outline"
              size="sm"
              onClick={completeSession}
              disabled={isCompleting}
              className="gap-2"
            >
              {isCompleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {isCompleting ? 'Completing...' : 'End Session'}
              </span>
            </Button>
          )}

          {/* Completed badge */}
          {isSessionCompleted && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              Completed
            </span>
          )}
        </div>
      </header>

      {/* Session completion error banner (AC-006 edge case: network error on complete) */}
      {completeError && (
        <div
          className="px-3 py-2 bg-red-50 border-b border-red-200 text-sm text-red-700 text-center"
          role="alert"
        >
          Could not end the session. Please try again.
        </div>
      )}

      {/* Scrollable message area */}
      <div
        ref={messageContainerRef}
        className="flex-1 overflow-y-auto px-3 md:px-4 py-4 space-y-1 bg-otter-paper"
      >
        {/* Empty state */}
        {(!messages || messages.length === 0) && (
          <div className="flex items-center justify-center h-full">
            <p className="text-center text-gray-500">Waiting for messages...</p>
          </div>
        )}

        {/* Message list */}
        {messages && messages.length > 0 && (
          <>
            {messages.map((msg: Message) => {
              // Image messages: use ImageMessageBubble
              if (msg.type === 'image' || msg.imageUrl) {
                return (
                  <ImageMessageBubble
                    key={msg.id}
                    message={msg}
                    isOwnMessage={msg.role === 'parent'}
                  />
                );
              }
              // Text messages: map 'parent' role to 'student' for MessageBubble alignment
              // (Option A — avoids modifying shared MessageBubble; PRD Invariant 4)
              const displayMsg: Message =
                msg.role === 'parent' ? { ...msg, role: 'student' as const } : msg;
              return <MessageBubble key={msg.id} message={displayMsg} sessionType="math_coach" />;
            })}

            {/* Typing indicator — context-aware for Math Coach (AC-005) */}
            {isWaitingForTutor && (
              <MathCoachTypingIndicator pollingPhase={pollingPhase} />
            )}
          </>
        )}

        {/* Session completed message */}
        {isSessionCompleted && (
          <div className="py-4 px-2 text-center text-sm text-gray-500">
            This session has ended. Return to the dashboard to start a new session.
          </div>
        )}

        {/* Send error banner */}
        {sendError && (
          <div
            className="mx-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between"
            role="alert"
            aria-live="polite"
          >
            <span>{sendError}</span>
            <button
              onClick={clearError}
              className="ml-2 text-red-500 hover:text-red-700 font-medium text-xs"
              aria-label="Dismiss error"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Reconnecting state is now handled by MathCoachTypingIndicator (US-005 AC-005) */}
      </div>

      {/* Input area — sticky at bottom */}
      {!isSessionCompleted && (
        <div className="border-t border-[#E7D8C8] bg-otter-paper sticky bottom-0 z-10 pb-[env(safe-area-inset-bottom)]">
          <div className="max-w-3xl mx-auto px-3 md:px-4 py-2 md:py-3">
            <div className="flex gap-2 items-end">
              {/* Camera / image upload button (left) */}
              <div className="flex-shrink-0">
                <ImageUpload
                  onImageReady={handleImageReady}
                  disabled={isSending || isWaitingForTutor}
                />
              </div>

              {/* Text input (right, flex-1) */}
              <div className="flex-1 min-w-0">
                <AnswerInput
                  onSubmit={sendTextMessage}
                  isSubmitting={isSending}
                  isSessionCompleted={false}
                  isWaitingForTutor={isWaitingForTutor}
                  error={null}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* When session is completed, show disabled input placeholder */}
      {isSessionCompleted && (
        <div className="border-t border-[#E7D8C8] bg-otter-paper sticky bottom-0 z-10 pb-[env(safe-area-inset-bottom)]">
          <div className="max-w-3xl mx-auto px-3 md:px-4 py-2 md:py-3">
            <AnswerInput
              onSubmit={() => {}}
              isSubmitting={false}
              isSessionCompleted={true}
              error={null}
            />
          </div>
        </div>
      )}

      {/* New message pill — floats above input bar when parent is reading above fold */}
      <NewMessagePill visible={pillVisible} onDismiss={scrollToBottom} />
    </div>
  );
}
